'use client'

/**
 * CaughtEarlyProof — a COMPACT, client-side "here's what the scanner caught early" strip for the
 * conversion DECISION moments (the free-tier wall and the subscribe gate).
 *
 * WHY (register: proof-at-decision-moments-0822). The timestamped catch receipts are the strongest
 * trust signal we have - proof the edge is real - but they lived on /subscribe and the top-of-funnel
 * pages, NOT at the paywall where people actually decide. The heavy server <CaughtEarlyStrip/> cannot
 * render inside the client PremiumContentGate, so this fetches the same data (via /api/caught-early
 * ?scope=recent, which stays populated and strong even off-hours) and renders a few lines.
 *
 * ⚠ IT DEGRADES TO NOTHING. Empty or a failed fetch -> renders null, so the gate is never left with a
 * broken or empty proof box.
 */

import { useEffect, useState } from 'react'
import { TrendingUp } from 'lucide-react'

type Catch = { symbol: string; dateLabel?: string; alertTime?: string; alertPrice: number; high: number; runPct: number }

const fmtPx = (n: number) => (n < 1 ? `$${n.toFixed(3)}` : `$${n.toFixed(2)}`)

export default function CaughtEarlyProof({ limit = 3 }: { limit?: number }) {
  const [rows, setRows] = useState<Catch[] | null>(null)

  useEffect(() => {
    let alive = true
    fetch('/api/caught-early?scope=recent')
      .then((r) => (r.ok ? r.json() : null))
      .then((j: { rows?: Catch[] } | null) => { if (alive) setRows(Array.isArray(j?.rows) ? j!.rows : []) })
      .catch(() => {})
    return () => { alive = false }
  }, [])

  if (!rows || rows.length === 0) return null
  const picks = rows.slice(0, Math.max(1, limit))

  return (
    <div className="mt-4 rounded-xl border border-amber-600/25 bg-amber-500/[0.05] p-3">
      <div className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-300">
        <TrendingUp className="h-3.5 w-3.5" /> Caught early by the scanner
      </div>
      <ul className="space-y-1.5">
        {picks.map((c) => (
          <li key={c.symbol} className="flex items-center justify-between gap-2 text-[12px]">
            <span className="min-w-0 truncate text-zinc-700 dark:text-zinc-300">
              <span className="font-black text-zinc-900 dark:text-white">${c.symbol}</span> first alerted{' '}
              <span className="tabular-nums text-zinc-800 dark:text-zinc-200">{fmtPx(c.alertPrice)}</span>
              {c.dateLabel ? <span className="text-zinc-500"> · {c.dateLabel}</span> : null}
            </span>
            <span className="shrink-0 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-1.5 py-0.5 text-[11px] font-bold tabular-nums text-emerald-700 dark:text-emerald-300">
              +{Math.round(c.runPct)}%
            </span>
          </li>
        ))}
      </ul>
      <p className="mt-2 text-[10px] leading-snug text-zinc-600">First alert price vs the session high after it. Not a realized trade.</p>
    </div>
  )
}
